import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePlansApi } from '../../../common/hooks/use-plans-api.hook';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';
import { useBillingApi } from '../../../common/hooks/use-billing-api.hook';
import { VendorPlan } from '../../../modules/superadmin/our-vendors/models/vendor.model';
import { VendorInvoice, StripeInvoiceSummary } from '../../../common/models/billing.model';
import { PageContainer } from '../../../common/components/PageContainer';
import { VendorPlanUsageCard } from './components/vendor-plan-usage-card';
import { AccountSummaryCard } from './components/account-summary-card';
import { BillingHistoryTable } from './components/billing-history-table';
import { AccessBanner } from '../../../modules/vendor/pricing/AccessBanner';

/**
 * Vendor Billing page: the account overview (current subscription + next/last
 * bill), plan usage, and the wire-transfer invoice history. Choosing / changing
 * a plan — and paying an accepted quote — lives on the separate Upgrade page.
 */
const BillingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const plansApi = usePlansApi();
  const billingApi = useBillingApi();
  const { planSlug, hasActiveSubscription, subscriptionCurrentPeriodEnd, wire } = useVendorPlan();

  const [plans, setPlans] = useState<VendorPlan[]>([]);
  const [invoices, setInvoices] = useState<VendorInvoice[] | null>(null);
  const [stripeInvoices, setStripeInvoices] = useState<StripeInvoiceSummary[]>([]);

  const historySectionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    plansApi.getPlans()
      .then((rows) => setPlans((rows as VendorPlan[] | undefined) || []))
      .catch(() => { /* summary falls back to the free-plan state */ });
    billingApi.getMyInvoices()
      .then((rows) => setInvoices(rows || []))
      .catch(() => setInvoices([]));
    // Stripe (card) invoices — merged into the same history table. Non-fatal:
    // vendors without a Stripe customer just get an empty list.
    billingApi.getMyStripeInvoices()
      .then((rows) => setStripeInvoices(rows || []))
      .catch(() => setStripeInvoices([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentPlan = useMemo(() => plans.find((p) => p.slug === planSlug), [plans, planSlug]);
  const isTopPlan = !!currentPlan && plans.every((p) => (p.sortOrder ?? 0) <= (currentPlan.sortOrder ?? 0));

  const handlePortal = async () => {
    try {
      const { url } = await billingApi.createPortalSession();
      window.location.href = url;
    } catch {
      /* ignore — button stays clickable */
    }
  };

  const breadcrumbs = [{ label: t('BILLING.TITLE', 'Billing') }];

  return (
    <PageContainer breadcrumbs={breadcrumbs}>
      <div>
        {/* Trial running / ended, or the agreement this vendor is on. */}
        <AccessBanner className="mb-6 md:mb-8" />

        {/* Account overview + usage, merged into a single card */}
        <div className="mb-6 overflow-hidden rounded-xl border bg-card md:mb-8">
          <AccountSummaryCard
            embedded
            isTopPlan={isTopPlan}
            currentPlan={currentPlan}
            invoices={invoices}
            hasActiveSubscription={hasActiveSubscription}
            subscriptionCurrentPeriodEnd={subscriptionCurrentPeriodEnd}
            wire={wire}
            onUpgrade={() => navigate('/home/billing/upgrade')}
            onManage={handlePortal}
            onViewHistory={() => historySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          />

          <VendorPlanUsageCard embedded />
        </div>

        <div ref={historySectionRef} className="mt-8 md:mt-10">
          <BillingHistoryTable invoices={invoices} stripeInvoices={stripeInvoices} />
        </div>
      </div>
    </PageContainer>
  );
};

export default BillingPage;
